import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import Escudo from "@/components/site/Escudo";
import PeruRibbon from "@/components/site/PeruRibbon";

export default function Footer() {
  const { t, localePath, tArray } = useI18n();

  return (
    <footer className="bg-[#0E2A47] text-white" data-testid="site-footer">
      <PeruRibbon height={4} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Escudo size={48} title={t("header.escudo_alt")} />
            <div>
              <div className="font-serif text-2xl">{t("brand.name")}</div>
              <div className="text-xs text-white/60 uppercase tracking-[0.14em] mt-1">
                {t("brand.tagline")}
              </div>
            </div>
          </div>
          <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-md" data-testid="footer-about">
            {t("footer.about_description")}
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-sm uppercase tracking-[0.18em] text-white/70">
            {t("footer.explore_title")}
          </h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link to={localePath("/")} className="hover:text-[#3FB68A]" data-testid="footer-link-home">
                {t("nav.home")}
              </Link>
            </li>
            <li>
              <a href={`${localePath("/")}#programa`} className="hover:text-[#3FB68A]" data-testid="footer-link-program">
                {t("nav.program")}
              </a>
            </li>
            <li>
              <a href={`${localePath("/")}#como-funciona`} className="hover:text-[#3FB68A]" data-testid="footer-link-how">
                {t("nav.how_it_works")}
              </a>
            </li>
            <li>
              <a href={`${localePath("/")}#especialistas`} className="hover:text-[#3FB68A]" data-testid="footer-link-advisors">
                {t("nav.advisors")}
              </a>
            </li>
            <li>
              <a href={`${localePath("/")}#newsletter`} className="hover:text-[#3FB68A]" data-testid="footer-link-newsletter">
                {t("nav.newsletter")}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-sm uppercase tracking-[0.18em] text-white/70">
            {t("footer.legal_title")}
          </h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link to={localePath("/privacy")} className="hover:text-[#3FB68A]" data-testid="footer-link-privacy">
                {t("privacy.title")}
              </Link>
            </li>
            <li>
              <Link to={localePath("/cookies")} className="hover:text-[#3FB68A]" data-testid="footer-link-cookies">
                {t("cookie_policy.title")}
              </Link>
            </li>
            <li>
              <Link to={localePath("/terms")} className="hover:text-[#3FB68A]" data-testid="footer-link-terms">
                {t("terms.title")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-sm uppercase tracking-[0.18em] text-white/70">
            {t("footer.contact_title")}
          </h4>
          <ul className="mt-5 space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#3FB68A]" aria-hidden="true" />
              <span data-testid="footer-address">{t("footer.address")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#3FB68A]" aria-hidden="true" />
              <span data-testid="footer-phone">{t("footer.phone")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#3FB68A]" aria-hidden="true" />
              <a
                href={`mailto:${t("footer.email")}`}
                className="hover:text-[#3FB68A]"
                data-testid="footer-email"
              >
                {t("footer.email")}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#3FB68A]" aria-hidden="true" />
              <span data-testid="footer-hours">{t("footer.hours")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid gap-4 md:grid-cols-12 text-xs text-white/60">
          <div className="md:col-span-8 space-y-1" data-testid="footer-legal-block">
            <div className="text-white/80 font-medium">{t("footer.legal.company_name")}</div>
            <div>{t("footer.legal.ruc")}</div>
            <div>{t("footer.legal.registration")}</div>
            <div>{t("footer.legal.license")}</div>
            <div>{t("footer.legal.incorporation")}</div>
            <div>{t("footer.legal.jurisdiction")}</div>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div data-testid="footer-copyright">{t("footer.copyright")}</div>
          </div>
        </div>
      </div>
      <PeruRibbon height={4} />
    </footer>
  );
}
